import { Box, Button, Typography } from "@mui/material";
import type { NikolaAction, NikolaActivity, NikolaMessagePart, NikolaSourceCitation } from "@chessla/shared";
import type { ChatStoreMessage, MessageBoardState } from "../messaging/chat.store";
import { InlineBoard } from "../board/InlineBoard";
import { CorrectionNotice } from "./CorrectionNotice";
import { MarkdownMessage } from "./MarkdownMessage";
import { ChatActions } from "./ChatActions";
import { NikolaActivityReceipt } from "./NikolaActivityReceipt";
import { NikolaEvidenceCard } from "./NikolaEvidenceCard";
import { NikolaCoachingAnswer } from "./NikolaCoachingAnswer";
import { NikolaMind } from "./NikolaMind";
import { NikolaSources } from "./NikolaSources";
import { expandNikolaMessageParts, type RenderableNikolaPart } from "../messaging/chat-message-parts";
import { gameReviewUrl } from "../messaging/chat-handoff";

const EMBEDDED_MOVE_PREFIX = /^For MOVE \d+ · PLY \d+ · [^·\r\n]+ · (?:MY|OPPONENT) MOVE: /;

export const embeddedUserMessageContent = (content: string): string => content.replace(EMBEDDED_MOVE_PREFIX, "");

const boardState = (part: Extract<NikolaMessagePart, { type: "board" }>): MessageBoardState => ({
  fen: part.fen,
  orientation: part.orientation === "w" ? "white" : "black",
  arrows: [],
  highlights: [],
  gameId: null,
  plyRange: null,
  compare: null,
  evalLineId: null,
});
const evidenceReference = (evidence: Extract<NikolaMessagePart, { type: "evidence" }>["evidence"]) =>
  evidence.reference ??
  (evidence.gameId
    ? {
        gameId: evidence.gameId,
        ...(evidence.ply === undefined ? {} : { ply: evidence.ply }),
        ...(evidence.fen === undefined ? {} : { fen: evidence.fen }),
      }
    : null);
const sourceKey = (source: NikolaSourceCitation): string => {
  if ((source.kind === "memory" || source.kind === "chessla") && source.game)
    return `game:${gameReviewUrl(source.game)}`;
  if (source.kind === "official_web" || source.kind === "web") return `web:${source.url}`;
  if (source.kind === "memory") return `memory:${source.sessionId ?? source.label}`;
  return `chessla:${source.label}`;
};
const compactReferences = (parts: readonly RenderableNikolaPart[], embedded: boolean): RenderableNikolaPart[] => {
  const limit = embedded ? 3 : 8;
  const sources: NikolaSourceCitation[] = [];
  const targets = new Set<string>();
  const remaining: RenderableNikolaPart[] = [];
  let firstCitationIndex: number | null = null;
  const addSource = (source: NikolaSourceCitation) => {
    const key = sourceKey(source);
    if (targets.has(key) || sources.length >= limit) return;
    targets.add(key);
    sources.push(source);
  };
  for (const part of parts) {
    if (embedded && (part.type === "board" || part.type === "board-marker")) continue;
    if (part.type === "sources") {
      firstCitationIndex ??= remaining.length;
      part.sources.forEach(addSource);
      continue;
    }
    if (part.type === "evidence") {
      const reference = evidenceReference(part.evidence);
      if (reference) {
        firstCitationIndex ??= remaining.length;
        addSource({ kind: "memory", label: part.evidence.title, game: reference });
        continue;
      }
      if (embedded) continue;
    }
    remaining.push(part);
  }
  if (!sources.length) return remaining;
  let lastNarrative = -1;
  remaining.forEach((part, index) => {
    if (part.type === "text" || part.type === "coaching") lastNarrative = index;
  });
  const insertion =
    lastNarrative >= 0 ? lastNarrative + 1 : Math.min(firstCitationIndex ?? remaining.length, remaining.length);
  remaining.splice(insertion, 0, { type: "sources", sources });
  return remaining;
};

const narrativeActivityKind = (activity: NikolaActivity): "commentary" | "reasoning_summary" | null => {
  const kind = String(activity.kind);
  return kind === "commentary" || kind === "reasoning_summary" ? kind : null;
};

const narrativeActivityText = (activity: NikolaActivity): string => {
  const detail = activity.detail?.trim();
  return detail?.length ? detail : activity.label;
};

export const ChatTurn = ({
  message,
  dayLabel,
  username,
  disabled,
  estimatedCredits,
  placeholder,
  embedded,
  onAction,
  onRetry,
  children,
}: {
  message: ChatStoreMessage;
  dayLabel?: string;
  username?: string | null;
  disabled?: boolean;
  estimatedCredits?: number | null;
  placeholder?: boolean;
  embedded?: boolean;
  onAction: (action: NikolaAction) => void;
  onRetry?: () => void;
  children?: React.ReactNode;
}) => {
  const expandedParts = message.parts?.length ? expandNikolaMessageParts(message.parts) : [];
  const visibleParts = expandedParts.filter((part) => part.type !== "activity");
  const activities = expandedParts.filter(
    (part): part is Extract<RenderableNikolaPart, { type: "activity" }> => part.type === "activity",
  );
  const narrativeActivities = activities.filter((part) => narrativeActivityKind(part.activity));
  const mindTexts = narrativeActivities.map((part) => narrativeActivityText(part.activity));
  const toolActivities = activities.filter(
    (part) => !narrativeActivityKind(part.activity) && part.activity.status !== "failed",
  );
  const runningActivities = toolActivities.filter((part) => part.activity.status === "running");
  const currentActivity = runningActivities.at(-1);
  const completedActivities = toolActivities.filter(
    (part) => part.activity.status === "complete" && part.activity.kind !== "coach_thinking",
  );
  const renderedParts = compactReferences(visibleParts, embedded === true && message.role === "assistant");
  const finalParts = renderedParts;
  const activityOnly = message.role === "assistant" && placeholder;
  const renderPart = (part: RenderableNikolaPart, index: number) => {
    if (part.type === "text") return <MarkdownMessage key={index} content={part.markdown} />;
    if (part.type === "board-marker")
      return <InlineBoard key={index} board={part.board} title={part.board.title} username={username} />;
    if (part.type === "activity") return <NikolaActivityReceipt key={index} activity={part.activity} />;
    if (part.type === "coaching") return <NikolaCoachingAnswer key={index} answer={part.coaching} />;
    if (part.type === "evidence") return <NikolaEvidenceCard key={index} evidence={part.evidence} compact={embedded} />;
    if (part.type === "sources") return <NikolaSources key={index} sources={part.sources} />;
    if (part.type === "actions")
      return (
        <ChatActions
          key={index}
          actions={part.actions}
          disabled={disabled}
          estimatedCredits={estimatedCredits}
          embedded={embedded}
          onAction={onAction}
        />
      );
    return <InlineBoard key={index} board={boardState(part)} username={username} />;
  };
  const activityTimeline = completedActivities.length ? (
    <Box
      component="details"
      data-testid="nikola-activity-timeline"
      open={activityOnly}
      sx={{ mt: 1, "& > summary": { cursor: "pointer", color: "text.secondary", fontSize: ".64rem", fontWeight: 850 } }}
    >
      <Box component="summary">
        {completedActivities.length} {completedActivities.length === 1 ? "check" : "checks"}
      </Box>
      <Box sx={{ mt: 0.75 }}>{completedActivities.map(renderPart)}</Box>
    </Box>
  ) : null;

  const day = dayLabel && (
    <Typography
      sx={{
        my: 1.5,
        textAlign: "center",
        color: "text.secondary",
        fontSize: ".6rem",
        fontWeight: 800,
        letterSpacing: ".1em",
      }}
    >
      {dayLabel}
    </Typography>
  );
  if (activityOnly)
    return (
      <>
        {day}
        {(mindTexts.length > 0 || toolActivities.length > 0 || children) && (
          <Box
            component="article"
            data-testid="chat-activity-lane"
            sx={{
              width: "100%",
              maxWidth: 960,
              mx: "auto",
              mb: 2,
              display: "flex",
              gap: { xs: 1, sm: 1.5 },
              alignItems: "flex-start",
            }}
          >
            <Box
              data-testid="chat-active-nikola"
              data-identity-surface="paper"
              aria-label="Nikola is working"
              sx={{
                display: "grid",
                placeItems: "center",
                flex: "0 0 auto",
                width: { xs: 38, sm: 46 },
                height: { xs: 38, sm: 46 },
                bgcolor: "primary.main",
                color: "background.default",
                fontSize: { xs: 22, sm: 28 },
                fontWeight: 900,
                lineHeight: 1,
                animation: "nikola-bounce 1s ease-in-out infinite",
                "@keyframes nikola-bounce": {
                  "0%, 100%": { transform: "translateY(0)" },
                  "50%": { transform: "translateY(-5px)" },
                },
                "@media (prefers-reduced-motion: reduce)": { animation: "none" },
              }}
            >
              <Box
                component="span"
                data-testid="nikola-thinking-mark"
                sx={{
                  display: "inline-block",
                  lineHeight: 1,
                }}
              >
                ♞
              </Box>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <NikolaMind texts={mindTexts} active />
              {currentActivity ? <NikolaActivityReceipt activity={currentActivity.activity} /> : null}
              {activityTimeline}
              {children}
            </Box>
          </Box>
        )}
      </>
    );
  return (
    <>
      {day}
      <Box
        component="article"
        sx={{
          width: "100%",
          maxWidth: 1020,
          mx: "auto",
          mb: message.role === "user" ? 2.5 : 3.25,
          display: "flex",
          flexDirection: message.role === "user" ? "row-reverse" : "row",
          gap: { xs: 1, sm: 1.5 },
          alignItems: "flex-start",
          minWidth: 0,
          contentVisibility: "auto",
          containIntrinsicSize: "auto 220px",
        }}
      >
        <Box
          data-testid={message.role === "user" ? "chat-player-identity" : "chat-nikola-identity"}
          data-identity-surface="paper"
          aria-label={message.role === "user" ? "You, player" : "Nikola, coach"}
          sx={{
            display: "grid",
            placeItems: "center",
            flex: "0 0 auto",
            width: { xs: 38, sm: 46 },
            height: { xs: 38, sm: 46 },
            bgcolor: "primary.main",
            color: "background.default",
            fontSize: { xs: 22, sm: 28 },
            fontWeight: 900,
            lineHeight: 1,
          }}
        >
          {message.role === "user" ? (
            <Box
              component="span"
              data-testid="chat-player-glyph"
              sx={{ display: "block", lineHeight: 1, transform: "translateY(-1px)" }}
            >
              ♟
            </Box>
          ) : (
            "♞"
          )}
        </Box>
        <Box
          data-testid={message.role === "assistant" ? "chat-assistant-bubble" : "chat-player-bubble"}
          data-chat-surface="current"
          sx={
            message.role === "user"
              ? {
                  maxWidth: "min(82%, 38rem)",
                  px: { xs: 1.25, sm: 1.6 },
                  py: 1.25,
                  border: "1px solid",
                  borderColor: "rgba(240,236,226,.36)",
                  bgcolor: "rgba(240,236,226,.045)",
                  backgroundImage: "linear-gradient(145deg,rgba(240,236,226,.045),transparent 70%)",
                  overflowWrap: "anywhere",
                }
              : {
                  width: "100%",
                  minWidth: 0,
                  p: { xs: 1.25, sm: 1.75 },
                  border: "1px solid",
                  borderColor: "rgba(240,236,226,.18)",
                  borderInlineStart: "3px solid",
                  borderInlineStartColor: "rgba(240,236,226,.42)",
                  bgcolor: "rgba(240,236,226,.018)",
                  backgroundImage: "linear-gradient(135deg,rgba(240,236,226,.035),transparent 42%)",
                  overflowWrap: "anywhere",
                }
          }
        >
          {message.role === "assistant" ? (
            <Typography
              sx={{ mb: 1.1, color: "text.secondary", fontSize: ".56rem", fontWeight: 950, letterSpacing: ".13em" }}
            >
              NIKOLA
            </Typography>
          ) : null}
          {message.role === "assistant" ? <NikolaMind texts={mindTexts} active={false} /> : null}
          {message.role === "assistant" ? activityTimeline : null}
          {message.role === "user" ? (
            <Typography sx={{ fontSize: ".82rem", lineHeight: 1.55, whiteSpace: "pre-wrap" }}>
              {embedded ? embeddedUserMessageContent(message.content) : message.content}
            </Typography>
          ) : finalParts.length ? (
            finalParts.map(renderPart)
          ) : message.segments?.length ? (
            message.segments.map((segment, index) =>
              segment.type === "text" ? (
                <MarkdownMessage key={index} content={segment.content} />
              ) : (
                <InlineBoard key={index} board={segment} title={segment.title} username={username} />
              ),
            )
          ) : !placeholder ? (
            <MarkdownMessage content={message.content} />
          ) : null}
          {message.role === "assistant" && (message.creditsCharged ?? 0) > 0 && !placeholder && (
            <Typography sx={{ mt: 1.25, color: "text.secondary", fontSize: ".62rem", fontWeight: 700 }}>
              {message.creditsCharged} credits used
            </Typography>
          )}
          {message.warnings?.length ? <CorrectionNotice warnings={message.warnings} /> : null}
          {message.failedDraft && (
            <Button size="small" variant="text" onClick={onRetry} disabled={disabled} sx={{ mt: 0.5, borderRadius: 0 }}>
              Retry
            </Button>
          )}
          {children}
        </Box>
      </Box>
    </>
  );
};
